'use client';

import { useState, useEffect } from 'react';
import SolutionCard from './SolutionCard';
import SolutionDetailModal from './SolutionDetailModal';
import ConsultationModal from './ConsultationModal';

interface Solution {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  features: string[];
  industries: string[];
  fabricOptions: string[];
  customizationOptions: string[];
  qualityStandards: string[];
  leadTime: string;
  minimumOrder: string;
}

export default function SolutionsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const solutions: Solution[] = [
  {
    id: 1,
    title: "Corporate Banking Uniforms",
    description: "Premium banking attire that projects professionalism and trust. Our corporate banking uniforms combine elegant design with practical functionality for daily banking operations.",
    image: "/assets/images/Screenshot_2026-02-25_at_11.56.04-1772010204819.jpg",
    alt: "Professional banker in navy blue suit and white shirt standing confidently in modern bank branch",
    features: [
    "Tailored fit for professional appearance",
    "Wrinkle-resistant fabrics for all-day wear",
    "Custom embroidery with bank logo",
    "Multiple style options for different roles",
    "Comfortable designs for customer-facing staff",
    "Durable materials for long-lasting use",
    "Color coordination with brand identity",
    "Easy-care fabrics for busy professionals"],

    industries: ["Banking", "Corporate"],
    fabricOptions: [
    "Premium wool blend for executive wear",
    "Poly-cotton blend for daily operations",
    "Stretch fabrics for comfort and mobility",
    "Stain-resistant treatments available"],

    customizationOptions: [
    "Custom logo embroidery placement",
    "Personalized name tags and badges",
    "Department-specific color coding",
    "Branch identification elements",
    "Executive vs. staff differentiation",
    "Seasonal fabric variations"],

    qualityStandards: [
    "ISO 9001 certified production",
    "Colorfastness testing for longevity",
    "Shrinkage control processes",
    "Seam strength verification",
    "Button and zipper durability testing"],

    leadTime: "4-6 weeks from design approval",
    minimumOrder: "50 units per style"
  },
  {
    id: 2,
    title: "Aviation Crew Uniforms",
    description: "Sophisticated aviation uniforms designed for comfort during long flights while maintaining the prestigious image of your airline brand.",
    image: "/assets/images/Screenshot_2026-02-25_at_11.36.17-1772008771595.jpg",
    alt: "Flight attendant in elegant navy uniform with red scarf standing in aircraft cabin with professional smile",
    features: [
    "Breathable fabrics for long-haul comfort",
    "Wrinkle-free materials for pristine appearance",
    "Multiple pocket configurations for functionality",
    "Weather-appropriate layering options",
    "Easy-care fabrics for frequent washing",
    "Professional styling that commands respect",
    "Comfortable fit for extended wear",
    "Durable construction for daily use"],

    industries: ["Aviation"],
    fabricOptions: [
    "Lightweight wool for cabin crew",
    "Technical fabrics with moisture-wicking",
    "Four-season weight options",
    "Flame-retardant treatments for safety"],

    customizationOptions: [
    "Airline logo embroidery and patches",
    "Rank insignia and wing badges",
    "Personalized name tags",
    "Department-specific accessories",
    "Seasonal uniform variations",
    "Special occasion uniform options"],

    qualityStandards: [
    "Aviation safety compliance",
    "International quality certifications",
    "Flame resistance testing",
    "Colorfastness under UV exposure",
    "Durability testing for frequent use"],

    leadTime: "6-8 weeks from design approval",
    minimumOrder: "100 units per style"
  },
  {
    id: 3,
    title: "Government Sector Uniforms",
    description: "Authoritative and dignified uniforms for government employees that reflect the importance of public service while ensuring comfort and durability.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14405cf40-1768381621643.png",
    alt: "Government official in formal dark uniform with badge standing in front of national flag",
    features: [
    "Formal designs for official duties",
    "Durable fabrics for daily wear",
    "Weather-resistant options available",
    "Multiple style variations for different departments",
    "Easy identification through design elements",
    "Comfortable for extended wear",
    "Professional appearance maintenance",
    "Cost-effective for large-scale orders"],

    industries: ["Government"],
    fabricOptions: [
    "Heavy-duty cotton blends",
    "Weather-resistant treated fabrics",
    "Breathable materials for tropical climate",
    "Reinforced fabrics for field work"],

    customizationOptions: [
    "Department-specific insignia",
    "Rank and position identification",
    "Government seal embroidery",
    "ID badge integration",
    "Reflective elements for safety",
    "Seasonal uniform variations"],

    qualityStandards: [
    "Government procurement standards",
    "Durability testing for extended use",
    "Colorfastness certification",
    "Seam strength verification",
    "Compliance with national standards"],

    leadTime: "8-10 weeks for large orders",
    minimumOrder: "200 units per style"
  },
  {
    id: 4,
    title: "Corporate Office Attire",
    description: "Modern corporate uniforms that balance professionalism with contemporary style, perfect for creating a cohesive brand image across your organization.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19112d11d-1767871904291.png",
    alt: "Business professionals in matching corporate attire collaborating in modern glass-walled office space",
    features: [
    "Contemporary designs for modern workplaces",
    "Comfortable fabrics for all-day wear",
    "Mix-and-match options for variety",
    "Professional appearance with style flexibility",
    "Easy-care materials for busy professionals",
    "Brand color integration",
    "Seasonal collection options",
    "Inclusive sizing for all body types"],

    industries: ["Corporate"],
    fabricOptions: [
    "Premium cotton blends",
    "Stretch fabrics for comfort",
    "Wrinkle-resistant treatments",
    "Breathable materials for office environments"],

    customizationOptions: [
    "Company logo embroidery",
    "Department color coding",
    "Personalized name embroidery",
    "Style variations by role",
    "Seasonal fabric options",
    "Accessory coordination"],

    qualityStandards: [
    "ISO quality certification",
    "Fabric durability testing",
    "Color consistency verification",
    "Shrinkage control processes",
    "Seam strength testing"],

    leadTime: "4-6 weeks from design approval",
    minimumOrder: "50 units per style"
  },
  {
    id: 5,
    title: "Hospitality Uniforms",
    description: "Elegant hospitality uniforms that enhance guest experience while providing comfort and functionality for your staff in hotels, restaurants, and resorts.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d0afe0b-1767511797948.png",
    alt: "Hotel staff in elegant burgundy uniforms with gold accents standing in luxurious hotel lobby",
    features: [
    "Elegant designs that impress guests",
    "Stain-resistant fabrics for food service",
    "Comfortable for long shifts",
    "Easy-care materials for frequent washing",
    "Professional appearance maintenance",
    "Multiple style options for different roles",
    "Durable construction for daily use",
    "Brand-aligned color schemes"],

    industries: ["Hospitality"],
    fabricOptions: [
    "Stain-resistant treated fabrics",
    "Breathable materials for active work",
    "Easy-care poly-cotton blends",
    "Premium fabrics for luxury properties"],

    customizationOptions: [
    "Hotel logo embroidery",
    "Department-specific styling",
    "Personalized name tags",
    "Role-based design variations",
    "Seasonal uniform options",
    "Special event uniform designs"],

    qualityStandards: [
    "Hospitality industry standards",
    "Stain resistance testing",
    "Colorfastness certification",
    "Durability for frequent washing",
    "Comfort testing for extended wear"],

    leadTime: "5-7 weeks from design approval",
    minimumOrder: "75 units per style"
  },
  {
    id: 6,
    title: "Healthcare Professional Attire",
    description: "Functional and hygienic healthcare uniforms designed for medical professionals who need comfort, mobility, and easy maintenance in demanding environments.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14f9cccf8-1768418464665.png",
    alt: "Medical professionals in clean white and blue scrubs standing in modern hospital corridor",
    features: [
    "Antimicrobial fabric treatments",
    "Easy-care materials for frequent washing",
    "Comfortable designs for long shifts",
    "Multiple pocket configurations",
    "Fluid-resistant options available",
    "Professional appearance in clinical settings",
    "Durable construction for daily use",
    "Breathable fabrics for comfort"],

    industries: ["Healthcare"],
    fabricOptions: [
    "Antimicrobial treated fabrics",
    "Fluid-resistant materials",
    "Breathable cotton blends",
    "Easy-care synthetic blends"],

    customizationOptions: [
    "Hospital logo embroidery",
    "Department color coding",
    "Personalized name embroidery",
    "Role-specific design elements",
    "Pocket configuration options",
    "Seasonal fabric weights"],

    qualityStandards: [
    "Healthcare industry compliance",
    "Antimicrobial effectiveness testing",
    "Colorfastness after repeated washing",
    "Durability testing for clinical use",
    "Comfort verification for extended wear"],

    leadTime: "4-6 weeks from design approval",
    minimumOrder: "100 units per style"
  },
  {
    id: 7,
    title: "Retail Staff Uniforms",
    description: "Stylish retail uniforms that create a professional brand image while ensuring your staff remains comfortable during customer interactions and store operations.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_111ab0ec3-1766816181335.png",
    alt: "Retail staff in matching branded polo shirts and dark pants assisting customers in modern store",
    features: [
    "Brand-aligned designs and colors",
    "Comfortable for standing and movement",
    "Easy-care fabrics for daily wear",
    "Professional appearance that builds trust",
    "Multiple style options for variety",
    "Durable construction for active work",
    "Seasonal collection options",
    "Cost-effective for large teams"],

    industries: ["Retail"],
    fabricOptions: [
    "Durable poly-cotton blends",
    "Breathable materials for comfort",
    "Wrinkle-resistant treatments",
    "Stain-resistant options available"],

    customizationOptions: [
    "Company logo embroidery",
    "Store location identification",
    "Personalized name tags",
    "Department-specific colors",
    "Seasonal design variations",
    "Promotional uniform options"],

    qualityStandards: [
    "Retail industry standards",
    "Durability testing for active wear",
    "Colorfastness certification",
    "Shrinkage control processes",
    "Comfort testing for extended wear"],

    leadTime: "4-5 weeks from design approval",
    minimumOrder: "50 units per style"
  },
  {
    id: 8,
    title: "Security Personnel Uniforms",
    description: "Authoritative security uniforms that command respect while providing functionality and comfort for protection professionals in various security roles.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aac3e107-1768480009072.png",
    alt: "Security officer in dark professional uniform with badge and equipment standing at building entrance",
    features: [
    "Authoritative design for security presence",
    "Durable fabrics for demanding work",
    "Multiple pocket configurations for equipment",
    "Weather-resistant options available",
    "Comfortable for extended shifts",
    "Professional appearance maintenance",
    "Reinforced construction for durability",
    "Easy identification through design"],

    industries: ["Corporate", "Government"],
    fabricOptions: [
    "Heavy-duty cotton blends",
    "Weather-resistant treated fabrics",
    "Breathable materials for comfort",
    "Reinforced fabrics for durability"],

    customizationOptions: [
    "Company logo and patches",
    "Rank and position insignia",
    "Reflective elements for visibility",
    "ID badge integration",
    "Equipment attachment points",
    "Seasonal uniform variations"],

    qualityStandards: [
    "Security industry standards",
    "Durability testing for demanding use",
    "Colorfastness certification",
    "Seam strength verification",
    "Weather resistance testing"],

    leadTime: "5-7 weeks from design approval",
    minimumOrder: "75 units per style"
  }];


  const handleLearnMore = (solution: Solution) => {
    if (!isHydrated) return;
    setSelectedSolution(solution);
    setIsDetailModalOpen(true);
  };

  const handleRequestConsultation = () => {
    if (!isHydrated) return;
    setIsDetailModalOpen(false);
    setIsConsultationModalOpen(true);
  };

  if (!isHydrated) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="animate-pulse">
          <div className="h-12 bg-muted rounded w-3/4 mb-4"></div>
          <div className="h-6 bg-muted rounded w-1/2 mb-8"></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) =>
            <div key={i} className="bg-card rounded-lg shadow-md overflow-hidden">
                <div className="h-64 bg-muted"></div>
                <div className="p-6">
                  <div className="h-6 bg-muted rounded mb-3"></div>
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded mb-4"></div>
                  <div className="h-10 bg-muted rounded"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution) =>
          <SolutionCard
            key={solution.id}
            title={solution.title}
            description={solution.description}
            image={solution.image}
            alt={solution.alt}
            features={solution.features}
            industries={solution.industries}
            onLearnMore={() => handleLearnMore(solution)} />

          )}
        </div>
      </div>
      
      <SolutionDetailModal
        solution={selectedSolution}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        onRequestConsultation={handleRequestConsultation} />

      
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
        selectedSolution={selectedSolution?.title} />

    </>
  );

}